import React, { useState } from 'react';
import {
  Star,
  Clock,
  MapPin,
  Zap,
  Sparkles,
  Heart,
  ShieldCheck,
  Tag,
  Check,
} from 'lucide-react';
export const BrowseView = ({
  restaurants,
  onSelectRestaurant,
  selectedCategory,
  onSelectCategory,
  searchQuery,
  isExpressMode,
  categories,
  loadingRestaurantId,
}) => {
  const [dietaryFilter, setDietaryFilter] = useState('all');
  const [favorites, setFavorites] = useState({});
  const [copiedPromo, setCopiedPromo] = useState(false);
  const copyPromoCode = code => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(code).catch(() => {});
    }
    setCopiedPromo(true);
    setTimeout(() => setCopiedPromo(false), 2500);
  };
  const toggleFavorite = (e, id) => {
    e.stopPropagation();
    setFavorites(prev => ({ ...prev, [id]: !prev[id] }));
  };
  // Filter restaurants based on category, search, and express mode
  const safeRestaurants = restaurants || [];
  const filteredRestaurants = safeRestaurants.filter(r => {
    // category match
    const categoryList = Array.isArray(r.categories) ? r.categories : [];
    const rCategory = String(r.category || '').toLowerCase();
    const selected = String(selectedCategory || 'all').toLowerCase();
    if (selected !== 'all' && rCategory !== selected) {
      const hasCat = categoryList.some(c =>
        String(c)
          .toLowerCase()
          .includes(selected),
      );
      if (!hasCat) return false;
    }
    // search query match
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchName = String(r.name || '')
        .toLowerCase()
        .includes(q);
      const matchCuisine = String(r.cuisine || '')
        .toLowerCase()
        .includes(q);
      const matchTagline = String(r.tagline || '')
        .toLowerCase()
        .includes(q);
      const matchCategories = categoryList.some(c =>
        String(c)
          .toLowerCase()
          .includes(q),
      );
      const matchMenu =
        Array.isArray(r.menu) &&
        r.menu.some(
          m =>
            String(m.name || '')
              .toLowerCase()
              .includes(q) ||
            String(m.description || '')
              .toLowerCase()
              .includes(q),
        );
      if (
        !matchName &&
        !matchCuisine &&
        !matchTagline &&
        !matchCategories &&
        !matchMenu
      )
        return false;
    }
    // express mode filter: 25 min or less
    if (isExpressMode) {
      const isFast = r.noticeHours <= 4 || r.distanceMiles <= 1.0;
      if (!isFast) return false;
    }
    // quick dietary
    if (dietaryFilter === 'veg') {
      const cuisine = String(r.cuisine || '').toLowerCase();
      const hasVegCategory = categoryList.some(c =>
        String(c)
          .toLowerCase()
          .includes('veg'),
      );
      const hasVegMenuItem =
        Array.isArray(r.menu) && r.menu.some(m => m.isVegetarian);
      return hasVegCategory || hasVegMenuItem || cuisine.includes('veg');
    }
    if (dietaryFilter === 'fast') {
      return r.distanceMiles <= 1.0;
    }
    if (dietaryFilter === 'rated') {
      return r.rating >= 4.8;
    }
    return true;
  });
  const newlyListed = safeRestaurants.filter(r => r.isNew);
  return React.createElement(
    'div',
    { className: 'panda-browse pb-16 space-y-8' },
    React.createElement(
      'section',
      {
        className:
          'relative overflow-hidden bg-gradient-to-br from-[#7B0D0D] via-[#991B1B] to-[#B91C1C] text-white shadow-2xl border border-red-700/60',
      },
      React.createElement(
        'div',
        {
          className: 'absolute inset-0 z-0 pointer-events-none overflow-hidden',
        },
        React.createElement('img', {
          src: '/hero_banner.jpg',
          alt: 'Artisanal Culinary Spread',
          className:
            'w-full h-full object-cover object-right md:object-center opacity-50 md:opacity-65 scale-105 transition-transform duration-1000 ease-out',
          referrerPolicy: 'no-referrer',
        }),
        React.createElement('div', {
          className:
            'absolute inset-0 bg-gradient-to-t from-[#700B0B] via-[#8B0E0E]/85 to-[#991B1B]/40 md:bg-gradient-to-r md:from-[#700B0B] md:via-[#8B0E0E]/85 md:to-red-900/30',
        }),
        React.createElement('div', {
          className:
            'absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-panda-red/25 via-transparent to-red-950/40',
        }),
      ),
      React.createElement(
        'div',
        {
          className:
            'relative z-10 max-w-7xl mx-auto px-6 py-8 sm:py-12 flex flex-col md:flex-row items-center justify-between gap-8',
        },
        React.createElement(
          'div',
          { className: 'max-w-xl text-center md:text-left space-y-4' },
          React.createElement(
            'div',
            {
              className:
                'inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-black/30 backdrop-blur-md border border-white/20 text-xs font-semibold tracking-wide text-white shadow-sm',
            },
            React.createElement('img', {
              src: '/panda_logo.jpg',
              alt: 'Panda on Wheels Logo',
              className:
                'w-5 h-5 rounded-full object-cover ring-1 ring-panda-soft/60',
              referrerPolicy: 'no-referrer',
            }),
            React.createElement(
              'span',
              { className: 'font-brand font-bold text-white tracking-normal' },
              'Panda On Wheels',
            ),
            React.createElement(
              'span',
              { className: 'text-white/40' },
              '\u2022',
            ),
            React.createElement(
              'span',
              { className: 'text-panda-soft font-bold' },
              'Fast \u2022 Fresh \u2022 Delicious',
            ),
          ),
          React.createElement(
            'h1',
            {
              className:
                'text-3xl sm:text-4xl lg:text-5xl font-heading font-extrabold tracking-tight text-white leading-[1.15]',
            },
            'Artisanal Flavors, ',
            React.createElement('br', null),
            React.createElement(
              'span',
              { className: 'text-panda-soft font-brand drop-shadow-sm' },
              'Delivered Express.',
            ),
          ),
          React.createElement(
            'p',
            {
              className:
                'text-red-50 text-sm sm:text-base leading-relaxed max-w-lg font-normal drop-shadow-xs',
            },
            'Indulge in chef-crafted bento boxes, steaming authentic ramen, artisan smash burgers, wood-fired pizza, and fresh boba \u2014 prepared to order and rushed hot to your doorstep in minutes.',
          ),
          React.createElement(
            'div',
            {
              className:
                'flex flex-wrap items-center gap-2.5 p-2.5 bg-black/30 backdrop-blur-md rounded-2xl border border-white/20 shadow-xl max-w-lg',
            },
            React.createElement(
              'div',
              {
                className:
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-panda-red text-white text-xs font-extrabold uppercase tracking-wider shadow-xs',
              },
              React.createElement(Tag, {
                className: 'w-3.5 h-3.5 text-white',
              }),
              React.createElement('span', null, 'Special Offer'),
            ),
            React.createElement(
              'div',
              {
                className:
                  'text-xs text-white flex items-center gap-2 font-medium',
              },
              React.createElement('span', null, '30% off with code'),
              React.createElement(
                'span',
                {
                  className:
                    'font-mono font-bold text-panda-soft bg-white/10 px-2.5 py-0.5 rounded border border-white/20 tracking-wider',
                },
                'PANDA30',
              ),
            ),
            React.createElement(
              'button',
              {
                type: 'button',
                onClick: () => copyPromoCode('PANDA30'),
                className:
                  'ml-auto inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-1.5 rounded-xl bg-white hover:bg-panda-tint text-[#991B1B] transition-all shadow-md active:scale-95 cursor-pointer',
              },
              copiedPromo
                ? React.createElement(
                    React.Fragment,
                    null,
                    React.createElement(Check, {
                      className: 'w-3.5 h-3.5 text-emerald-600',
                    }),
                    React.createElement(
                      'span',
                      { className: 'text-emerald-700' },
                      'Copied!',
                    ),
                  )
                : React.createElement(
                    React.Fragment,
                    null,
                    React.createElement(Sparkles, {
                      className: 'w-3.5 h-3.5 text-panda-red',
                    }),
                    React.createElement('span', null, 'Copy Code'),
                  ),
            ),
          ),
          React.createElement(
            'div',
            { className: 'flex flex-wrap items-center gap-2 pt-1' },
            React.createElement(
              'span',
              { className: 'text-xs text-red-100 font-semibold mr-1' },
              'Quick Filter:',
            ),
            React.createElement(
              'button',
              {
                type: 'button',
                onClick: () =>
                  setDietaryFilter(dietaryFilter === 'fast' ? 'all' : 'fast'),
                className: `px-3 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 shadow-sm cursor-pointer ${
                  dietaryFilter === 'fast'
                    ? 'bg-panda-red text-white shadow-md font-bold'
                    : 'bg-black/25 hover:bg-black/40 text-white border border-white/20 backdrop-blur-xs'
                }`,
              },
              React.createElement(Zap, {
                className: `w-3.5 h-3.5 ${
                  dietaryFilter === 'fast' ? 'text-white' : 'text-panda-soft'
                }`,
              }),
              React.createElement('span', null, '\u26A1 Under 25 Mins'),
            ),
            React.createElement(
              'button',
              {
                type: 'button',
                onClick: () =>
                  setDietaryFilter(dietaryFilter === 'rated' ? 'all' : 'rated'),
                className: `px-3 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 shadow-sm cursor-pointer ${
                  dietaryFilter === 'rated'
                    ? 'bg-panda-red text-white shadow-md font-bold'
                    : 'bg-black/25 hover:bg-black/40 text-white border border-white/20 backdrop-blur-xs'
                }`,
              },
              React.createElement(Star, {
                className: `w-3.5 h-3.5 ${
                  dietaryFilter === 'rated'
                    ? 'text-white fill-white'
                    : 'text-panda-soft fill-panda-soft'
                }`,
              }),
              React.createElement('span', null, '\u2B50 4.8+ Rated'),
            ),
            React.createElement(
              'button',
              {
                type: 'button',
                onClick: () =>
                  setDietaryFilter(dietaryFilter === 'veg' ? 'all' : 'veg'),
                className: `px-3 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 shadow-sm cursor-pointer ${
                  dietaryFilter === 'veg'
                    ? 'bg-panda-red text-white shadow-md font-bold'
                    : 'bg-black/25 hover:bg-black/40 text-white border border-white/20 backdrop-blur-xs'
                }`,
              },
              React.createElement('span', null, '\uD83C\uDF31'),
              React.createElement('span', null, 'Plant & Veggie'),
            ),
            dietaryFilter !== 'all' &&
              React.createElement(
                'button',
                {
                  type: 'button',
                  onClick: () => setDietaryFilter('all'),
                  className:
                    'text-xs text-red-200 hover:text-white underline ml-1 cursor-pointer font-medium',
                },
                'Clear filter',
              ),
          ),
        ),
        React.createElement(
          'div',
          {
            className:
              'relative w-full max-w-xs sm:max-w-sm flex flex-col justify-center items-center md:items-end',
          },
          React.createElement(
            'div',
            {
              className:
                'w-full max-w-xs bg-black/35 backdrop-blur-xl rounded-2xl p-5 border border-white/20 shadow-2xl space-y-4',
            },
            React.createElement(
              'div',
              { className: 'flex items-center gap-3.5' },
              React.createElement(
                'div',
                {
                  className:
                    'w-14 h-14 rounded-full overflow-hidden shadow-md border-2 border-white ring-2 ring-panda-red/40 flex-shrink-0 bg-white',
                },
                React.createElement('img', {
                  src: '/panda_logo.jpg',
                  alt: 'Panda on Wheels Official Badge',
                  className: 'w-full h-full object-cover',
                  referrerPolicy: 'no-referrer',
                }),
              ),
              React.createElement(
                'div',
                { className: 'min-w-0 flex-1' },
                React.createElement(
                  'div',
                  { className: 'flex items-center gap-1.5' },
                  React.createElement(
                    'span',
                    {
                      className:
                        'text-xs font-bold text-white uppercase tracking-wider',
                    },
                    'Express Fleet',
                  ),
                  React.createElement('span', {
                    className:
                      'inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse',
                  }),
                ),
                React.createElement(
                  'p',
                  { className: 'text-xs text-red-100 font-semibold' },
                  'Panda Priority Delivery',
                ),
                React.createElement(
                  'p',
                  { className: 'text-[11px] text-red-200/80' },
                  'Heated & insulated thermal lock',
                ),
              ),
            ),
            React.createElement(
              'div',
              { className: 'grid grid-cols-2 gap-2.5 pt-1' },
              React.createElement(
                'div',
                {
                  className:
                    'bg-white/10 rounded-xl p-3 border border-white/10 text-center backdrop-blur-sm',
                },
                React.createElement(
                  'div',
                  {
                    className:
                      'text-lg font-extrabold text-panda-soft font-heading',
                  },
                  '~24 min',
                ),
                React.createElement(
                  'div',
                  {
                    className:
                      'text-[10px] text-red-100/90 uppercase tracking-wider font-bold',
                  },
                  'Average Drop',
                ),
              ),
              React.createElement(
                'div',
                {
                  className:
                    'bg-white/10 rounded-xl p-3 border border-white/10 text-center backdrop-blur-sm',
                },
                React.createElement(
                  'div',
                  {
                    className:
                      'text-lg font-extrabold text-panda-soft font-heading',
                  },
                  '4.9 \u2605',
                ),
                React.createElement(
                  'div',
                  {
                    className:
                      'text-[10px] text-red-100/90 uppercase tracking-wider font-bold',
                  },
                  'Taste & Quality',
                ),
              ),
            ),
            React.createElement(
              'div',
              {
                className:
                  'flex items-center justify-between px-3 py-2 rounded-xl bg-white/10 border border-white/10 text-xs text-white backdrop-blur-sm',
              },
              React.createElement(
                'span',
                {
                  className:
                    'flex items-center gap-1.5 font-semibold text-red-50',
                },
                React.createElement(ShieldCheck, {
                  className: 'w-4 h-4 text-emerald-400',
                }),
                'Freshness Guaranteed',
              ),
              React.createElement(
                'span',
                {
                  className:
                    'text-[11px] font-extrabold text-white bg-panda-red px-2 py-0.5 rounded-full border border-panda-soft text-center inline-block',
                  style: { width: '90px' },
                },
                '100% Hot',
              ),
            ),
          ),
        ),
      ),
    ),
    React.createElement(
      'section',
      { className: 'space-y-3' },
      React.createElement(
        'div',
        { className: 'flex items-center justify-between' },
        React.createElement(
          'h2',
          {
            className:
              'text-lg font-heading font-bold text-[#202124] flex items-center gap-2',
          },
          React.createElement('span', null, 'Explore Cuisines'),
          React.createElement(
            'span',
            { className: 'text-xs text-[#E53935] font-normal' },
            'Swipe to see more \u2192',
          ),
        ),
        selectedCategory !== 'all' &&
          React.createElement(
            'button',
            {
              onClick: () => onSelectCategory('all'),
              className: 'text-xs font-semibold text-[#E53935] hover:underline',
            },
            'Reset to All',
          ),
      ),
      React.createElement(
        'div',
        {
          className:
            'flex items-center gap-2.5 overflow-x-auto pb-2 pt-1 px-1 hide-scrollbar',
        },
        categories.map(cat => {
          const isSelected = selectedCategory === cat.id;
          return React.createElement(
            'button',
            {
              key: cat.id,
              onClick: () => onSelectCategory(cat.id),
              className: `flex-shrink-0 flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-heading font-semibold transition-all shadow-2xs ${
                isSelected
                  ? 'bg-[#E53935] text-white ring-2 ring-[#E53935]/40 shadow-sm transform scale-105'
                  : 'bg-white hover:bg-panda-tint/80 text-[#202124] border border-gray-200/80'
              }`,
            },
            React.createElement('span', { className: 'text-base' }, cat.icon),
            React.createElement('span', null, cat.name),
          );
        }),
      ),
      React.createElement(
        'div',
        { className: 'flex flex-wrap items-center gap-2 pt-1 px-1' },
        React.createElement(
          'button',
          {
            onClick: () => setDietaryFilter('all'),
            className: `px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              dietaryFilter === 'all'
                ? 'bg-[#202124] text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`,
          },
          'All Choices',
        ),
        React.createElement(
          'button',
          {
            onClick: () => setDietaryFilter('veg'),
            className: `px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 transition-colors ${
              dietaryFilter === 'veg'
                ? 'bg-[#65A30D] text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`,
          },
          React.createElement('span', null, '\uD83C\uDF31 Vegetarian Options'),
        ),
        React.createElement(
          'button',
          {
            onClick: () => setDietaryFilter('fast'),
            className: `px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 transition-colors ${
              dietaryFilter === 'fast'
                ? 'bg-panda-red text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`,
          },
          React.createElement('span', null, '\u26A1 Nearby (< 1 mi)'),
        ),
        React.createElement(
          'button',
          {
            onClick: () => setDietaryFilter('rated'),
            className: `px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 transition-colors ${
              dietaryFilter === 'rated'
                ? 'bg-red-500 text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`,
          },
          React.createElement('span', null, '\u2B50 Top Rated (4.8+)'),
        ),
      ),
    ),
    newlyListed.length > 0 &&
      selectedCategory === 'all' &&
      !searchQuery &&
      React.createElement(
        'section',
        { className: 'space-y-3' },
        React.createElement(
          'div',
          { className: 'flex items-center justify-between' },
          React.createElement(
            'h3',
            {
              className:
                'text-xl font-heading font-bold text-[#202124] flex items-center gap-2',
            },
            React.createElement('span', null, 'Newly listed!'),
            React.createElement('span', {
              className: 'w-2 h-2 rounded-full bg-[#E53935] animate-ping',
            }),
          ),
          React.createElement(
            'span',
            { className: 'text-xs text-gray-500' },
            'Fresh additions to Panda network',
          ),
        ),
        React.createElement(
          'div',
          { className: 'grid grid-cols-1 sm:grid-cols-2 gap-4' },
          newlyListed.map(res =>
            React.createElement(
              'div',
              {
                key: res.id,
                onClick: res.availableForOrdering === false
                  ? undefined
                  : () => onSelectRestaurant(res),
                className: `group bg-white rounded-2xl overflow-hidden border border-gray-200/80 hover:border-[#E53935]/40 hover:shadow-md transition-all flex flex-col sm:flex-row ${
                  res.availableForOrdering === false
                    ? 'opacity-60 cursor-not-allowed'
                    : 'cursor-pointer'
                }`,
              },
              React.createElement(
                'div',
                {
                  className:
                    'relative w-full sm:w-48 h-40 sm:h-auto flex-shrink-0 overflow-hidden',
                },
                React.createElement('img', {
                  src: res.coverImage,
                  alt: res.name,
                  className:
                    'w-full h-full object-cover group-hover:scale-105 transition-transform duration-300',
                  referrerPolicy: 'no-referrer',
                  onError: e => {
                    e.target.onerror = null;
                    e.target.src =
                      res.fallbackImage || '/assets/img/samples/meal_1.jpg';
                  },
                }),
                React.createElement(
                  'span',
                  {
                    className:
                      'absolute top-2 left-2 bg-[#E53935] text-white text-[10px] font-bold px-2 py-0.5 rounded-md shadow-xs',
                  },
                  'New!',
                ),
              ),
              React.createElement(
                'div',
                { className: 'p-4 flex flex-col justify-between flex-1' },
                React.createElement(
                  'div',
                  null,
                  React.createElement(
                    'div',
                    { className: 'flex items-center justify-between' },
                    React.createElement(
                      'h4',
                      {
                        className:
                          'font-heading font-bold text-base text-[#202124] group-hover:text-[#E53935] transition-colors',
                      },
                      res.name,
                    ),
                    React.createElement(
                      'span',
                      { className: 'text-xs font-semibold text-gray-500' },
                      res.distanceMiles,
                      ' mi',
                    ),
                  ),
                  React.createElement(
                    'p',
                    { className: 'text-xs text-gray-500 mt-1 line-clamp-2' },
                    res.tagline,
                  ),
                ),
                React.createElement(
                  'div',
                  {
                    className:
                      'mt-3 pt-2 border-t border-gray-100 flex items-center justify-between text-xs text-gray-600',
                  },
                  React.createElement(
                    'span',
                    { className: 'flex items-center gap-1' },
                    React.createElement(MapPin, {
                      className: 'w-3 h-3 text-[#E53935]',
                    }),
                    React.createElement(
                      'span',
                      { className: 'truncate max-w-[150px]' },
                      res.address.split(',')[0],
                    ),
                  ),
                  res.availableForOrdering === false
                    ? React.createElement(
                        'span',
                        { className: 'font-bold text-gray-400' },
                        'Unavailable',
                      )
                    : React.createElement(
                        'span',
                        {
                          className:
                            'font-bold text-[#E53935] flex items-center gap-1',
                        },
                        'Explore Menu \u2192',
                      ),
                ),
              ),
            ),
          ),
        ),
      ),
    React.createElement(
      'section',
      { className: 'space-y-4' },
      React.createElement(
        'div',
        null,
        React.createElement(
          'h3',
          { className: 'text-xl font-heading font-bold text-[#202124]' },
          'All restaurants',
        ),
        React.createElement(
          'p',
          { className: 'text-xs text-gray-500 mt-0.5' },
          'Live availability first, with real client ratings shown whenever a restaurant has earned them.',
        ),
      ),
      filteredRestaurants.length === 0
        ? React.createElement(
            'div',
            {
              className:
                'bg-white rounded-2xl p-10 text-center space-y-3 border border-gray-200',
            },
            React.createElement(
              'p',
              { className: 'text-base font-bold text-gray-700' },
              'No restaurants found matching your criteria.',
            ),
            React.createElement(
              'p',
              { className: 'text-xs text-gray-500' },
              'Try clearing filters or searching for something else like "Bento" or "Chicken".',
            ),
            React.createElement(
              'button',
              {
                onClick: () => {
                  onSelectCategory('all');
                  setDietaryFilter('all');
                },
                className:
                  'px-4 py-2 rounded-xl bg-[#E53935] text-white font-bold text-xs',
              },
              'Reset All Filters',
            ),
          )
        : React.createElement(
            'div',
            {
              className:
                'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 auto-rows-fr gap-4',
            },
            filteredRestaurants.map(res => {
              const isFav = !!favorites[res.id];
              const isLoading = loadingRestaurantId === res.id;
              const isUnavailable = res.availableForOrdering === false;
              return React.createElement(
                'div',
                {
                  key: res.id,
                  onClick: isUnavailable ? undefined : () => onSelectRestaurant(res),
                  className: `group h-full bg-white rounded-2xl overflow-hidden border border-gray-200/80 hover:border-[#E53935]/40 hover:shadow-lg transition-all duration-200 flex flex-col justify-between relative ${
                    isUnavailable ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                  }`,
                },
                React.createElement(
                  'div',
                  null,
                  React.createElement(
                    'div',
                    {
                      className:
                        'relative aspect-[4/3] overflow-hidden bg-gray-100',
                    },
                    React.createElement('img', {
                      src: res.coverImage,
                      alt: res.name,
                      className:
                        'w-full h-full object-cover group-hover:scale-105 transition-transform duration-300',
                      referrerPolicy: 'no-referrer',
                      onError: e => {
                        e.target.onerror = null;
                        e.target.src =
                          res.fallbackImage || '/assets/img/samples/meal_1.jpg';
                      },
                    }),
                    res.isNew &&
                      React.createElement(
                        'span',
                        {
                          className:
                            'absolute top-2.5 left-2.5 bg-[#E53935] text-white text-[10px] font-bold px-2 py-0.5 rounded-md shadow-xs',
                        },
                        'New!',
                      ),
                    isUnavailable &&
                      React.createElement(
                        'div',
                        {
                          className:
                            'absolute inset-0 bg-black/40 flex items-center justify-center',
                        },
                        React.createElement(
                          'span',
                          {
                            className:
                              'bg-white text-gray-700 text-xs font-bold px-3 py-1.5 rounded-md shadow-md',
                          },
                          'Unavailable',
                        ),
                      ),
                    React.createElement(
                      'button',
                      {
                        onClick: e => toggleFavorite(e, res.id),
                        className:
                          'absolute top-2.5 right-2.5 p-1.5 rounded-full bg-white/90 text-gray-600 hover:text-[#E53935] shadow-sm backdrop-blur-xs transition-colors',
                        'aria-label': 'Save to favorites',
                      },
                      React.createElement(Heart, {
                        className: `w-4 h-4 ${
                          isFav ? 'fill-[#E53935] text-[#E53935]' : ''
                        }`,
                      }),
                    ),
                    React.createElement(
                      'div',
                      {
                        className:
                          'absolute bottom-2 left-2.5 bg-[#202124]/85 backdrop-blur-xs text-white text-[11px] font-medium px-2 py-0.5 rounded-md flex items-center gap-1',
                      },
                      React.createElement(Clock, {
                        className: 'w-3 h-3 text-panda-soft',
                      }),
                      React.createElement('span', null, res.deliveryTime),
                      React.createElement('span', null, '\u2022'),
                      React.createElement(
                        'span',
                        null,
                        '$',
                        res.deliveryFee.toFixed(2),
                        ' delivery',
                      ),
                    ),
                  ),
                  React.createElement(
                    'div',
                    { className: 'p-4 space-y-2' },
                    React.createElement(
                      'div',
                      { className: 'flex items-start justify-between gap-2' },
                      React.createElement(
                        'div',
                        null,
                        React.createElement(
                          'h4',
                          {
                            className:
                              'font-heading font-bold text-base text-[#202124] group-hover:text-[#E53935] transition-colors leading-snug',
                          },
                          res.name,
                        ),
                        React.createElement(
                          'p',
                          { className: 'text-xs text-gray-500 font-medium' },
                          res.cuisine,
                        ),
                      ),
                      React.createElement(
                        'div',
                        {
                          className:
                            'flex items-center gap-1 bg-panda-tint px-2 py-0.5 rounded-lg border border-panda-soft/60 flex-shrink-0',
                        },
                        React.createElement(Star, {
                          className: 'w-3 h-3 fill-panda-red text-panda-red',
                        }),
                        React.createElement(
                          'span',
                          { className: 'text-xs font-bold text-panda-dark' },
                          res.rating,
                        ),
                        React.createElement(
                          'span',
                          { className: 'text-[10px] text-gray-400' },
                          '(',
                          res.reviewCount,
                          ')',
                        ),
                      ),
                    ),
                    React.createElement(
                      'p',
                      {
                        className:
                          'text-xs text-gray-600 line-clamp-2 leading-relaxed',
                      },
                      res.tagline,
                    ),
                  ),
                ),
                React.createElement(
                  'div',
                  {
                    className:
                      'px-4 pb-4 pt-2 border-t border-gray-100 flex items-center justify-between text-xs',
                  },
                  React.createElement(
                    'div',
                    {
                      className:
                        'flex items-center gap-1 text-gray-500 truncate max-w-[170px]',
                    },
                    React.createElement(MapPin, {
                      className: 'w-3.5 h-3.5 text-[#E53935] flex-shrink-0',
                    }),
                    React.createElement(
                      'span',
                      { className: 'truncate' },
                      res.address,
                    ),
                  ),
                  isUnavailable
                    ? React.createElement(
                        'span',
                        { className: 'font-heading font-bold text-gray-400' },
                        'Unavailable',
                      )
                    : React.createElement(
                        'span',
                        {
                          className:
                            'font-heading font-bold text-[#E53935] group-hover:translate-x-0.5 transition-transform flex items-center gap-1',
                        },
                        'View Menu \u2192',
                      ),
                ),
                isLoading &&
                  React.createElement(
                    'div',
                    {
                      className:
                        'absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-10',
                    },
                    React.createElement('div', {
                      className:
                        'w-8 h-8 border-3 border-[#E53935] border-t-transparent rounded-full animate-spin',
                    }),
                  ),
              );
            }),
          ),
    ),
  );
};
